import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import "./voice-glow.scss";

export const VoiceGlow: React.FC = () => {
    const { connected, isAiSpeaking, userVolume, isThinking, volume } = useLiveAPIContext();

    // Show glow when it's user's turn (connected and AI not speaking)
    const isUserTurn = connected && !isAiSpeaking;

    // Normalize volume for animations (0 to 1) - instant response
    const normalizedVolume = useMemo(() => Math.min(userVolume * 25, 1.5), [userVolume]);

    // Normalize AI volume for animations (0 to 1) - instant response
    const normalizedAiVolume = useMemo(() => Math.min(volume * 25, 1.5), [volume]);

    // Generate flowing waves for thinking animation
    const flowingWaves = useMemo(() => {
        const waves = [];
        for (let i = 0; i < 12; i++) {
            waves.push({
                id: i,
                top: 20 + Math.random() * 60,
                height: 2 + Math.random() * 4,
                duration: 1.5 + Math.random() * 2,
                delay: Math.random() * 2,
                opacity: 0.3 + Math.random() * 0.5,
            });
        }
        return waves;
    }, []);

    return (
        <>
            {/* User Turn Glow - Circular wave from bottom */}
            <AnimatePresence>
                {isUserTurn && (
                    <motion.div
                        className="user-glow-container"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Main circular glow - always visible with ambient animation */}
                        <motion.div
                            className="circular-glow main"
                            animate={{
                                scale: [1, 1.02, 1, 1.03, 1],
                                opacity: [0.5, 0.6, 0.5, 0.55, 0.5],
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            style={{
                                transform: `scale(${1 + normalizedVolume * 0.15})`,
                                opacity: 0.5 + normalizedVolume * 0.3,
                            }}
                        />

                        {/* Secondary wave layer - responds to voice */}
                        <motion.div
                            className="circular-glow secondary"
                            animate={{
                                scale: [1, 1.05, 0.98, 1.03, 1],
                                opacity: [0.3, 0.4, 0.35, 0.45, 0.3],
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: 0.5,
                            }}
                            style={{
                                transform: `scale(${1 + normalizedVolume * 0.2})`,
                                opacity: 0.3 + normalizedVolume * 0.4,
                            }}
                        />

                        {/* Inner bright core - most reactive to voice */}
                        <motion.div
                            className="circular-glow core"
                            style={{
                                transform: `scale(${0.8 + normalizedVolume * 0.3})`,
                                opacity: 0.6 + normalizedVolume * 0.4,
                            }}
                        />

                        {/* Ripple waves that expand on voice */}
                        {normalizedVolume > 0.1 && (
                            <>
                                <motion.div
                                    className="ripple-wave"
                                    initial={{ scale: 0.8, opacity: 0.5 }}
                                    animate={{ scale: 1.5, opacity: 0 }}
                                    transition={{
                                        duration: 1,
                                        repeat: Infinity,
                                        ease: "easeOut",
                                    }}
                                />
                                <motion.div
                                    className="ripple-wave"
                                    initial={{ scale: 0.8, opacity: 0.4 }}
                                    animate={{ scale: 1.8, opacity: 0 }}
                                    transition={{
                                        duration: 1.2,
                                        repeat: Infinity,
                                        ease: "easeOut",
                                        delay: 0.3,
                                    }}
                                />
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* AI Speaking Glow - Light Pink circular wave from bottom */}
            <AnimatePresence>
                {isAiSpeaking && (
                    <motion.div
                        className="ai-glow-container"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Main circular glow - responds to AI voice */}
                        <motion.div
                            className="circular-glow main"
                            animate={{
                                scale: [1, 1.02, 1, 1.03, 1],
                                opacity: [0.5, 0.6, 0.5, 0.55, 0.5],
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            style={{
                                transform: `scale(${1 + normalizedAiVolume * 0.15})`,
                                opacity: 0.5 + normalizedAiVolume * 0.3,
                            }}
                        />

                        {/* Secondary wave layer - responds to AI voice */}
                        <motion.div
                            className="circular-glow secondary"
                            animate={{
                                scale: [1, 1.05, 0.98, 1.03, 1],
                                opacity: [0.3, 0.4, 0.35, 0.45, 0.3],
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: 0.5,
                            }}
                            style={{
                                transform: `scale(${1 + normalizedAiVolume * 0.2})`,
                                opacity: 0.3 + normalizedAiVolume * 0.4,
                            }}
                        />

                        {/* Inner bright core - most reactive to AI voice */}
                        <motion.div
                            className="circular-glow core"
                            style={{
                                transform: `scale(${0.8 + normalizedAiVolume * 0.3})`,
                                opacity: 0.6 + normalizedAiVolume * 0.4,
                            }}
                        />

                        {/* Ripple waves that expand on AI voice */}
                        {normalizedAiVolume > 0.1 && (
                            <>
                                <motion.div
                                    className="ripple-wave"
                                    initial={{ scale: 0.8, opacity: 0.5 }}
                                    animate={{ scale: 1.5, opacity: 0 }}
                                    transition={{
                                        duration: 1,
                                        repeat: Infinity,
                                        ease: "easeOut",
                                    }}
                                />
                                <motion.div
                                    className="ripple-wave"
                                    initial={{ scale: 0.8, opacity: 0.4 }}
                                    animate={{ scale: 1.8, opacity: 0 }}
                                    transition={{
                                        duration: 1.2,
                                        repeat: Infinity,
                                        ease: "easeOut",
                                        delay: 0.3,
                                    }}
                                />
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Thinking - Flowing Waves Animation (Red) */}
            <AnimatePresence>
                {isThinking && (
                    <motion.div
                        className="thinking-flow-container"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {flowingWaves.map((wave) => (
                            <motion.div
                                key={wave.id}
                                className="flow-wave"
                                style={{
                                    top: `${wave.top}%`,
                                    height: `${wave.height}px`,
                                    opacity: wave.opacity,
                                }}
                                animate={{
                                    x: ["-100%", "100%"],
                                }}
                                transition={{
                                    duration: wave.duration,
                                    repeat: Infinity,
                                    delay: wave.delay,
                                    ease: "linear",
                                }}
                            />
                        ))}
                        <div className="flow-base-glow" />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
